import { persistentAtom } from '@nanostores/persistent';
import { atom, computed } from 'nanostores';
import type { TokenStats } from '~/lib/.server/rateLimit';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { getAllChats, removeChat, removeChats, type ChatHistoryItem } from '~/lib/persistence/chats';
import type { PromptMode } from '~/lib/prompts/types';
import { generalSettingsStore } from '~/lib/stores/settings';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { FileAttachment, FileSelection } from '~/types/chat';
import { EventEmitter } from '~/utils/event-emitter';
import { hmrPropertyFactory } from '~/utils/hmr';

const hmr = hmrPropertyFactory('ChatStore');

interface ChatEvents {
  sendChatMessage: { message: string; mode?: PromptMode; details?: Record<string, unknown> };
}

export type RequestStatus = 'PENDING' | 'COMPLETE' | 'ERROR' | 'NONE';

class ChatStore {
  @hmr()
  isReadOnly = atom<boolean>(false);

  @hmr()
  #specialTokenPromptVisibleChatSerializedState = persistentAtom<string>('designTokensPromptVisible', 'true');

  forceShowTokenInChat = computed(generalSettingsStore, (settings) => settings.showTokenUsageInChat);

  forceShowSubscribeBannerInChat = computed(
    generalSettingsStore,
    (settings) => settings.forceShowSubscribeBannerInChat,
  );

  specialTokenPromptVisibleSetting = computed(
    this.#specialTokenPromptVisibleChatSerializedState,
    (value) => value === 'true',
  );

  @hmr()
  promptMode = atom<PromptMode>('build');

  @hmr()
  tokenStats = atom<TokenStats | undefined>();

  @hmr()
  subscribeDismissed = atom<boolean>(false);

  @hmr()
  started = atom(false);

  @hmr()
  aborted = atom(false);

  @hmr()
  showChat = atom(true);

  @hmr()
  fileSelections = atom<FileSelection[]>([]);

  @hmr()
  attachments = atom<FileAttachment[]>([]);

  @hmr()
  requestStatus = atom<RequestStatus>('NONE');

  @hmr()
  chatList = atom<ChatHistoryItem[]>([]);

  @hmr()
  chatListLoading = atom(false);

  @hmr()
  hasMorePreviousMessages = atom(false);

  @hmr()
  isLoadingPreviousMessages = atom(false);

  readonly events = new EventEmitter<ChatEvents>();

  findChatByProjectId(projectId: string) {
    return this.chatList.get().find((chat) => chat.projectId === projectId);
  }

  setSpecialTokenPromptVisible(value: boolean) {
    this.#specialTokenPromptVisibleChatSerializedState.set(`${value}`);
  }

  resetAttachments() {
    this.attachments.set([]);
  }

  setTokenStats(tokenStats: TokenStats) {
    const prevStats = this.tokenStats.get();

    this.tokenStats.set(tokenStats);

    if (!prevStats) {
      return;
    }

    if (tokenStats.totalToday >= tokenStats.maxPerDay && prevStats.totalToday < tokenStats.maxPerDay) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.DailyTokenLimitReached,
        },
      });
    }

    if (
      tokenStats.regularTokens.used >= tokenStats.regularTokens.available &&
      prevStats.regularTokens.used < prevStats.regularTokens.available
    ) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.MonthlyTokenLimitReached,
        },
      });
    }
  }

  addAttachment(attachment: Omit<FileAttachment, 'id' | 'storageKey'>) {
    const internalAttachment: FileAttachment = { ...attachment, id: String(Date.now()), storageKey: 'dummy_key' };

    this.attachments.set([...this.attachments.get(), internalAttachment]);

    return internalAttachment;
  }

  updateAttachment(id: string, update: Partial<Omit<FileAttachment, 'id'>>) {
    this.attachments.set(
      this.attachments.get().map((attachment) => {
        if (attachment.id === id) {
          return { ...attachment, ...update };
        }

        return attachment;
      }),
    );
  }

  removeAttachment(attachmentToRemove: FileAttachment) {
    this.attachments.set(this.attachments.get().filter((attachment) => attachment.id !== attachmentToRemove.id));
  }

  addToSelection(fileSelection: FileSelection) {
    this.fileSelections.set([...this.fileSelections.get(), fileSelection]);
  }

  removeFileSelection(index: number) {
    this.fileSelections.set(this.fileSelections.get().filter((_, i) => i !== index));
  }

  resetFileSelections() {
    this.fileSelections.set([]);
  }

  async clearChatList() {
    this.chatList.set([]);
  }

  async loadChatList() {
    try {
      this.chatListLoading.set(true);

      const list = await getAllChats(userStore.getActiveOrganization().get());

      this.chatList.set(list.filter((item) => item.projectId && item.description));
    } catch {
      throw new Error('Failed to load your chats.');
    } finally {
      this.chatListLoading.set(false);
    }
  }

  async removeChat(itemToRemove: ChatHistoryItem) {
    const currentChatList = this.chatList.get();

    try {
      this.chatList.set(currentChatList.filter((item) => item.projectId !== itemToRemove.projectId));

      await removeChat(itemToRemove.projectId);
    } catch (error) {
      // restore the previous list if the deletion failed
      this.chatList.set(currentChatList);

      throw error;
    }
  }

  async removeAllChats() {
    const currentChatList = this.chatList.get();

    try {
      this.chatList.set([]);

      await removeChats(userStore.getActiveOrganization().get());
    } catch (error) {
      this.chatList.set(currentChatList);

      throw error;
    }
  }
}

export const chatStore = new ChatStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { chatStore });
}
